import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, DollarSign, TrendingUp, AlertCircle, XCircle, CreditCard } from 'lucide-react';
import { format } from 'date-fns';
import { PaymentTransaction } from '@/hooks/usePaymentTransactions';

interface PaymentStatusCardProps {
  paymentStatus: 'not_required' | 'payment_pending' | 'ach_pending' | 'payment_captured' | 'held_in_escrow' | 'paid_to_vendor' | 'payment_failed';
  transaction?: PaymentTransaction | null;
  showVendorFinancials?: boolean;
}

export function PaymentStatusCard({ paymentStatus, transaction, showVendorFinancials = false }: PaymentStatusCardProps) {
  const getStatusConfig = () => {
    // If transaction exists, use its status for more accurate state
    const effectiveStatus = transaction?.status || paymentStatus;
    
    // Check if funds have been released (transfer complete)
    if (effectiveStatus === 'transfer_complete') {
      return {
        label: 'Funds Released',
        icon: TrendingUp,
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        description: 'Payment released and transferred to vendor'
      };
    }
    
    switch (paymentStatus) {
      case 'not_required':
        return {
          label: 'Payment Not Required',
          icon: DollarSign,
          className: 'bg-muted text-muted-foreground',
          description: 'No payment is required for this job'
        };
      case 'payment_pending':
        return {
          label: 'Payment Pending',
          icon: Clock,
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
          description: 'Awaiting payment processing'
        };
      case 'ach_pending':
        return {
          label: 'ACH Payment Processing',
          icon: Clock,
          className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
          description: 'Bank transfer in progress - settlement in 3-5 business days'
        };
      case 'payment_captured':
      case 'held_in_escrow':
        return {
          label: 'Held in Escrow',
          icon: CheckCircle2,
          className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          description: 'Payment secured and held until job completion'
        };
      case 'paid_to_vendor':
        return {
          label: 'Paid to Vendor',
          icon: TrendingUp,
          className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
          description: 'Payment transferred to vendor'
        };
      case 'payment_failed':
        return {
          label: 'Payment Failed',
          icon: XCircle,
          className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
          description: 'Payment processing failed'
        };
      default:
        return {
          label: 'Unknown Status',
          icon: AlertCircle,
          className: 'bg-muted text-muted-foreground',
          description: 'Payment status unknown'
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  if (paymentStatus === 'not_required') {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Payment Status
        </CardTitle>
        <CardDescription>Transaction and escrow details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium">Initial PO Payment</p>
            <p className="text-sm text-muted-foreground">
              {statusConfig.description}
            </p>
          </div>
          <Badge className={`${statusConfig.className} flex items-center gap-1.5`}>
            <StatusIcon className="h-3 w-3" />
            {statusConfig.label}
          </Badge>
        </div>

        {transaction && (
          <div className="space-y-3 pt-2">
            {/* Payment Method Info */}
            {(transaction.payment_method_type || transaction.payment_method_last4) && (
              <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {transaction.payment_method_type === 'us_bank_account' 
                    ? 'Bank Account' 
                    : transaction.payment_method_type === 'stripe_invoice'
                    ? 'Invoice'
                    : 'Card'}
                  {transaction.payment_method_last4 && ` •••• ${transaction.payment_method_last4}`}
                </span>
              </div>
            )}

            <div className={`grid ${showVendorFinancials ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-lg font-semibold">
                  {formatAmount(
                    transaction.total_amount_cents + 
                    (transaction.tax_amount_cents || transaction.vendor_tax_cents || 0)
                  )}
                </p>
                {(transaction.tax_amount_cents || transaction.vendor_tax_cents || 0) > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatAmount(transaction.total_amount_cents)} + {formatAmount(transaction.tax_amount_cents || transaction.vendor_tax_cents || 0)} tax
                  </p>
                )}
              </div>
              {showVendorFinancials && (
                <div>
                  <p className="text-sm text-muted-foreground">Vendor Payout</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                    {formatAmount(transaction.vendor_payout_cents)}
                  </p>
                </div>
              )}
            </div>

            <div className={`grid ${showVendorFinancials ? 'grid-cols-2' : 'grid-cols-1'} gap-4 text-sm`}>
              {showVendorFinancials && (
                <div>
                  <p className="text-muted-foreground">Platform Fee</p>
                  <p className="font-medium">{formatAmount(transaction.platform_fee_cents)}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Currency</p>
                <p className="font-medium uppercase">{transaction.currency}</p>
              </div>
            </div>

            {/* ACH Settlement Date */}
            {transaction.status === 'ach_pending' && transaction.ach_settlement_expected_at && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">Expected Settlement</p>
                <p className="text-sm font-medium">
                  {format(new Date(transaction.ach_settlement_expected_at), 'PPP')}
                </p>
              </div>
            )}

            {transaction.payment_captured_at && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">Payment Captured</p>
                <p className="text-sm font-medium">
                  {format(new Date(transaction.payment_captured_at), 'PPP p')}
                </p>
              </div>
            )}

            {/* ACH Settled Date */}
            {transaction.ach_settled_at && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">ACH Settled</p>
                <p className="text-sm font-medium">
                  {format(new Date(transaction.ach_settled_at), 'PPP p')}
                </p>
              </div>
            )}

            {transaction.transfer_completed_at && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">Transferred to Vendor</p>
                <p className="text-sm font-medium">
                  {format(new Date(transaction.transfer_completed_at), 'PPP p')}
                </p>
              </div>
            )}

            {transaction.error_message && (
              <div className="pt-2 border-t">
                <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Error</p>
                    <p className="text-xs">{transaction.error_message}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ACH Processing Notice */}
            {transaction.status === 'ach_pending' && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>ACH Payment Processing:</strong> Your bank transfer is being processed. This typically takes 3-5 business days to settle. You'll be able to issue the Purchase Order once the payment is confirmed.
                </p>
              </div>
            )}

            {transaction.status === 'held_in_escrow' && (
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-900 dark:text-green-100">
                  <strong>Funds Secured:</strong> Payment is held in escrow and will be released to the vendor when you accept the completed work.
                </p>
              </div>
            )}

            {transaction.status === 'transfer_complete' && (
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-sm text-green-900 dark:text-green-100">
                  <strong>Funds Released:</strong> Payment has been released from escrow and transferred to the vendor.
                </p>
              </div>
            )}

            {transaction.stripe_payment_intent_id && (
              <div className="pt-2 text-xs text-muted-foreground">
                <p>Transaction ID: {transaction.stripe_payment_intent_id}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
